import React, { useState } from "react";
import { Users, KeyRound, Trash2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { db } from "@/api/base44Client";
import { CreateCodePanel } from "./CreateCodePanel";

const TITLES = { users: "Alle Nutzer", codes: "Alle Codes" };
export const run = async (fn) => {
  try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen"); }
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-CH") : "");

function UserRow({ u, onChange }) {
  const toggle = () => run(async () => {
    await db.admin.updateUser(u.user_id, { licensed: !u.licensed });
    toast.success(u.licensed ? "Nutzer gesperrt" : "Nutzer freigeschaltet");
    onChange();
  });
  return (
    <div className="adm-row" data-testid={`admin-user-row-${u.user_id}`}>
      <div className="adm-row-main">
        <div className="adm-row-title">{u.name || u.email} {u.role === "admin" && <span className="adm-pill">ADMIN</span>}</div>
        <div className="adm-row-meta">{u.email} · seit {fmt(u.created_at)}</div>
      </div>
      <span className={`adm-pill ${u.licensed ? "" : "adm-pill-off"}`}>{u.licensed ? "FREIGESCHALTET" : "GESPERRT"}</span>
      <div className="adm-row-actions">
        <button className="adm-btn adm-btn-ghost adm-btn-sm" data-testid={`admin-user-toggle-${u.user_id}`} onClick={toggle}>{u.licensed ? "Sperren" : "Freischalten"}</button>
      </div>
    </div>
  );
}

function CodeRow({ c, onChange }) {
  const toggle = () => run(async () => { await db.admin.updateLicense(c.id, { active: !c.active }); onChange(); });
  const remove = () => {
    if (!window.confirm(`Code ${c.code} löschen?`)) return;
    run(async () => { await db.admin.deleteLicense(c.id); toast.success("Code gelöscht"); onChange(); });
  };
  return (
    <div className="adm-row" data-testid={`admin-code-row-${c.code}`}>
      <div className="adm-row-main">
        <div className="adm-row-title adm-code">{c.code}</div>
        <div className="adm-row-meta">{c.note || "Keine Notiz"} · {c.uses || 0}/{c.max_uses ? c.max_uses : "∞"} genutzt{c.profession ? ` · ${c.profession}` : ""}</div>
      </div>
      <span className={`adm-pill ${c.active ? "" : "adm-pill-off"}`}>{c.active ? "AKTIV" : "INAKTIV"}</span>
      <div className="adm-row-actions">
        <button className="adm-btn adm-btn-ghost adm-btn-sm" data-testid={`admin-code-toggle-${c.code}`} onClick={toggle}>{c.active ? "Deaktivieren" : "Aktivieren"}</button>
        <button className="adm-btn adm-btn-ghost adm-btn-sm adm-btn-danger" data-testid={`admin-code-delete-${c.code}`} onClick={remove} aria-label="Code löschen"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export function AdminListPanel({ view, users, licenses, onChange }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  if (!view) return null;
  const isUsers = view === "users";
  const term = search.trim().toLowerCase();
  const rows = isUsers ? users.filter((u) => !term || `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(term)) : licenses;
  const Icon = isUsers ? Users : KeyRound;
  return (
    <section className="adm-card" data-testid={`admin-list-${view}`}>
      <div className="adm-card-head">
        <h2 className="adm-card-title"><span className="adm-card-icon"><Icon className="h-4 w-4" /></span>{TITLES[view]}</h2>
        <div className="adm-row-actions">
          <span className="adm-pill">{rows.length} Einträge</span>
          {!isUsers && <button className="adm-btn adm-btn-sm" data-testid="admin-create-code-btn" onClick={() => setCreateOpen((o) => !o)} aria-expanded={createOpen}><Plus className="h-3.5 w-3.5" /> Code erstellen</button>}
        </div>
      </div>
      {isUsers && (
        <div className="adm-search" style={{ marginBottom: "0.75rem" }}>
          <Search className="h-4 w-4" />
          <input className="adm-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nach Name oder E-Mail suchen…" data-testid="admin-user-search" />
        </div>
      )}
      {!isUsers && createOpen && <CreateCodePanel onCreated={onChange} onClose={() => setCreateOpen(false)} />}
      <div className="adm-list">
        {rows.length === 0 && <p className="adm-empty">Keine Einträge vorhanden.</p>}
        {isUsers ? rows.map((u) => <UserRow key={u.user_id} u={u} onChange={onChange} />) : rows.map((c) => <CodeRow key={c.id} c={c} onChange={onChange} />)}
      </div>
    </section>
  );
}
